import React from "react";
import { Table, Tag, Input, Select, Card, Row, Col, Space } from "antd";
import useHelperRequests from "hooks/helper-requests/useHelperRequests";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import { useNavigate } from "react-router-dom";
import useFormattedDate from "hooks/useFormatDate";
import { EyeOutlined } from "@ant-design/icons";
import CopyableText from "components/common/CopyableText";

const { Option } = Select;

export default function HelperRequestsPage() {

  const navigate = useNavigate();
  const {
    requests,
    stats,
    loading,

    page,
    pageSize,
    totalElements,

    setPage,
    setPageSize,
    setStatus,
    setQuery,
  } = useHelperRequests();
  const { formatDateTime } = useFormattedDate();

  const columns = [
    {
      title: "User ID",
      dataIndex: "id",
      width: 330,
      ellipsis: true,
      render: (id, record) => (
        <span
          className="user-id-cell"
          role="button"
          tabIndex={0}
          onClick={(e) => {
            if (e?.target?.closest?.(".ant-typography-copy")) return;
            navigate(`/olitask/users/${record.id}/helper-profile`);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              navigate(`/olitask/users/${record.id}/helper-profile`);
            }
          }}
          style={{ cursor: "pointer" }}
        >
          <CopyableText
            value={id}
            showTooltip={false}
            stopPropagation={false}
          />
        </span>
      ),
    },
    {
      title: "Name",
      width: 160,
      ellipsis: true,
      render: (_, record) =>
        record?.fullName ||
        [record?.firstName, record?.middleName, record?.lastName].filter(Boolean).join(" ") ||
        `${record?.firstName || ""} ${record?.lastName || ""}`.trim() ||
        record?.name ||
        "-",
    },
    {
      title: "Email",
      dataIndex: "email",
      width: 220,
      ellipsis: true,
      render: (_, record) => record?.email || record?.userEmail || "-",
    },
    {
      title: "Phone",
      dataIndex: "phone",
      width: 150,
    },
    {
      title: "Status",
      dataIndex: "helperStatus",
      render: (status) => {
        const value = status || "UNKNOWN";
        const color =
          value === "APPROVED"
            ? "green"
            : value === "VERIFIED"
            ? "green"
            : value === "PENDING"
            ? "orange"
            : value === "SUSPENDED"
            ? "red"
            : value === "TERMINATED" || value === "CANCELLED" || value === "DELETED"
            ? "red"
            : "blue";

        return (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Tag
              color={color}
              style={{
                marginInlineEnd: 0,
                padding: "2px 10px",
                borderRadius: 6,
                fontWeight: 600,
                fontSize: 11,
                textTransform: "uppercase",
                whiteSpace: "nowrap",
              }}
            >
              {value}
            </Tag>
          </div>
        );
      },
      width: 140,
      align: "center",
    },
  

    {
      title: "Submitted",
      width: 180,
      render: (_, r) => formatDateTime(r.signedUpAt),
    },
    
    {
      title: "Actions",
      width: 90,
      render: (_, record) => (
        <Space>
          <EyeOutlined
            style={{ cursor: "pointer", marginRight: 5 }}
            onClick={(e) => {
              e?.stopPropagation?.();
              navigate(`/olitask/users/${record.id}/helper-profile`);
            }}
          />
        </Space>
      ),
    },
  ];


  const StatCard = ({ label, value, color, bg }) => (
    <Card
      style={{
        borderLeft: `5px solid ${color}`,
        backgroundColor: bg,
      }}
    >
      <div style={{ fontSize: 12, color: "#888" }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: "bold" }}>{value}</div>
    </Card>
  );

  const applyStatusFilter = (nextStatus) => {
    setStatus(nextStatus);
    setPage(0);
  };

  return (
    <div className="container-fluid p-3">

      <div className="d-flex justify-content-between align-items-center mb-3 mt-3 gap-3">
          <WidgetSectionTitle
              icon="exchange-alt"
              title="Helper Profiles | Requests"
              subtitle="Review and manage all helper profiles and requests"
              transform="shrink-2"
              className="flex-1"
          />
      </div>

      {/* STATS CARDS */}
      <Row gutter={16} className="mb-3">
        <Col xs={12} sm={12} md={6} lg={4} xl={3}>
          <div
            role="button"
            tabIndex={0}
            onClick={() => applyStatusFilter(null)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") applyStatusFilter(null);
            }}
            style={{ cursor: "pointer" }}
          >
            <StatCard label="Total" value={stats?.total ?? 0} color="#1677ff" bg="#e6f4ff" />
          </div>
        </Col>

        <Col xs={12} sm={12} md={6} lg={4} xl={3}>
          <div
            role="button"
            tabIndex={0}
            onClick={() => applyStatusFilter("PENDING")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") applyStatusFilter("PENDING");
            }}
            style={{ cursor: "pointer" }}
          >
            <StatCard label="Pending" value={stats?.pending ?? 0} color="#fa8c16" bg="#fff7e6" />
          </div>
        </Col>

        <Col xs={12} sm={12} md={6} lg={4} xl={3}>
          <div
            role="button"
            tabIndex={0}
            onClick={() => applyStatusFilter("APPROVED")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") applyStatusFilter("APPROVED");
            }}
            style={{ cursor: "pointer" }}
          >
            <StatCard label="Approved" value={stats?.approved ?? 0} color="#52c41a" bg="#f6ffed" />
          </div>
        </Col>

        <Col xs={12} sm={12} md={6} lg={4} xl={3}>
          <div
            role="button"
            tabIndex={0}
            onClick={() => applyStatusFilter("VERIFIED")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") applyStatusFilter("VERIFIED");
            }}
            style={{ cursor: "pointer" }}
          >
            <StatCard label="Verified" value={stats?.verified ?? 0} color="#52c41a" bg="#f6ffed" />
          </div>
        </Col>

        <Col xs={12} sm={12} md={6} lg={4} xl={3}>
          <div
            role="button"
            tabIndex={0}
            onClick={() => applyStatusFilter("SUSPENDED")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") applyStatusFilter("SUSPENDED");
            }}
            style={{ cursor: "pointer" }}
          >
            <StatCard label="Suspended" value={stats?.suspended ?? 0} color="#722ed1" bg="#f9f0ff" />
          </div>
        </Col>

        <Col xs={12} sm={12} md={6} lg={4} xl={3}>
          <div
            role="button"
            tabIndex={0}
            onClick={() => applyStatusFilter("TERMINATED")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") applyStatusFilter("TERMINATED");
            }}
            style={{ cursor: "pointer" }}
          >
            <StatCard label="Terminated" value={stats?.terminated ?? 0} color="#ff4d4f" bg="#fff1f0" />
          </div>
        </Col>

        <Col xs={12} sm={12} md={6} lg={4} xl={3}>
          <div
            role="button"
            tabIndex={0}
            onClick={() => applyStatusFilter("CANCELLED")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") applyStatusFilter("CANCELLED");
            }}
            style={{ cursor: "pointer" }}
          >
            <StatCard label="Cancelled" value={stats?.cancelled ?? 0} color="#ff4d4f" bg="#fff1f0" />
          </div>
        </Col>

        <Col xs={12} sm={12} md={6} lg={4} xl={3}>
          <div
            role="button"
            tabIndex={0}
            onClick={() => applyStatusFilter("DELETED")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") applyStatusFilter("DELETED");
            }}
            style={{ cursor: "pointer" }}
          >
            <StatCard label="Deleted" value={stats?.deleted ?? 0} color="#ff4d4f" bg="#fff1f0" />
          </div>
        </Col>
      </Row>

      {/* FILTERS */}
      <Row gutter={12} className="mb-3">
        <Col span={6}>
          <Input
            placeholder="Search name/email..."
            onChange={(e) => setQuery(e.target.value)}
          />
        </Col>

        <Col span={4}>
          <Select
            allowClear
            placeholder="Filter status"
            onChange={(v) => setStatus(v)}
            style={{ width: "100%" }}
          >
            <Option value="NONE">None</Option>
            <Option value="PENDING">Pending</Option>
            <Option value="APPROVED">Approved</Option>
            <Option value="VERIFIED">Verified</Option>
            <Option value="SUSPENDED">Suspended</Option>
            <Option value="TERMINATED">Terminated</Option>
            <Option value="CANCELLED">Cancelled</Option>
            <Option value="DELETED">Deleted</Option>
          </Select>
        </Col>
      </Row>

      {/* TABLE */}
      <Table
        dataSource={requests}
        columns={columns}
        loading={loading}
        rowKey="id"
        tableLayout="fixed"
        scroll={{ x: 1400 }}
        className="custom-table"
        bordered
        pagination={{
          current: page + 1,
          pageSize,
          total: totalElements,
          onChange: (p, size) => {
            setPage(p - 1);
            setPageSize(size);
          },
        }}
        onRow={(record) => ({
            onClick: (e) => {
              if (e?.target?.closest?.(".ant-typography-copy")) return;
              navigate(`/olitask/users/${record.id}/helper-profile`);
            },
        })}
        size="small"
      />
    </div>
  );
}
